import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, BadgeCheck, Undo2, MessageSquareText, BookOpen } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { StatutBadge, Section, dateFr } from './shared';
import { PreuvesAppli, LiensSection, DocumentsSection } from './PreuvesSections';
import { AideIA } from './AideIA';
import { HistoriqueSection } from './HistoriqueSection';
import { ElementsAppliSection, NotesAuditSection } from './AuditSections';

const Referentiel = ({ d }) => (
  <Section titre="Ce que demande le référentiel" icone={BookOpen} testId="qualiopi-referentiel">
    <p className="text-sm text-slate-700">{d.libelle}</p>
    <div className="rounded-lg border-l-4 border-[#8DC63F] bg-slate-50 p-3 text-sm text-slate-700"><strong>Niveau attendu : </strong>{d.niveau_attendu}</div>
    <details className="group rounded-lg border border-slate-200 p-3 text-sm">
      <summary className="cursor-pointer font-medium text-[#0F172A]" data-testid="qualiopi-preuves-attendues-toggle">Éléments de preuve cités par le guide</summary>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-600">{d.preuves_attendues.map((p) => <li key={p}>{p}</li>)}</ul>
      {d.specificites.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-500 italic">{d.specificites.map((p) => <li key={p}>{p}</li>)}</ul>}
    </details>
  </Section>
);

const Commentaire = ({ d, onSave }) => {
  const [texte, setTexte] = useState(d.commentaire || '');
  useEffect(() => setTexte(d.commentaire || ''), [d.num, d.commentaire]);
  return (
    <Section titre="Commentaire du centre" icone={MessageSquareText} testId="qualiopi-commentaire">
      <Textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={3} placeholder="Où trouver la preuve, personne référente, action prévue…" data-testid="qualiopi-commentaire-input" />
      {texte !== (d.commentaire || '') && <Button size="sm" onClick={() => onSave({ commentaire: texte })} data-testid="qualiopi-commentaire-save">Enregistrer le commentaire</Button>}
    </Section>
  );
};

const PiedValidation = ({ d, busy, onSave }) => {
  const sansPreuve = d.statut === 'rouge';
  return (
    <div className="sticky -bottom-6 -mx-6 -mb-6 mt-6 space-y-3 border-t border-slate-200 bg-white/95 px-6 py-4 backdrop-blur" data-testid="qualiopi-pied">
      <label className="flex items-center justify-between gap-3 text-sm text-slate-600">
        <span>Non applicable à FISL (retiré du score)</span>
        <Switch checked={!!d.non_applicable} disabled={busy} onCheckedChange={(v) => onSave({ non_applicable: v })} data-testid="qualiopi-non-applicable" />
      </label>
      {!d.non_applicable && (d.valide ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
          <span className="flex items-center gap-2" data-testid="qualiopi-valide-info"><BadgeCheck className="h-5 w-5" /> Validé{d.valide_par_nom ? ` par ${d.valide_par_nom}` : ''} le {dateFr(d.valide_le)}</span>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onSave({ valide: false })} data-testid="qualiopi-devalider"><Undo2 className="h-4 w-4 mr-1" /> Retirer la validation</Button>
        </div>
      ) : (
        <>
          <Button onClick={() => onSave({ valide: true })} disabled={busy || sansPreuve} className="h-11 w-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="qualiopi-valider">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BadgeCheck className="h-5 w-5 mr-2" /> Je valide cet indicateur</>}
          </Button>
          {sansPreuve && <p className="text-center text-xs text-red-600" data-testid="qualiopi-valider-aide">Ajoutez au moins une preuve (lien ou document) pour pouvoir valider.</p>}
        </>
      ))}
    </div>
  );
};

export const IndicateurPanneau = ({ num, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setD(null);
    if (num) axios.get(`/qualiopi/indicateurs/${num}`).then((r) => setD(r.data)).catch(() => toast.error("Impossible de charger l'indicateur"));
  }, [num]);
  const maj = useCallback((detail) => { setD(detail); onChanged(); }, [onChanged]);
  const enregistrer = async (champs) => {
    setBusy(true);
    try {
      maj((await axios.put(`/qualiopi/indicateurs/${num}`, champs)).data);
      if (champs.valide) toast.success(`Indicateur ${num} validé`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!num} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="qualiopi-panneau">
        {!d ? <div className="flex h-full items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div> : (
          <div className="space-y-6">
            <SheetHeader className="space-y-2 pr-6 text-left">
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Indicateur {d.num} · Critère {d.critere} <StatutBadge statut={d.statut} testId="qualiopi-panneau-statut" />
                <span className={d.non_conformite === 'majeure' ? 'text-red-600' : ''}>NC {d.non_conformite}</span>
              </div>
              <SheetTitle className="font-display text-2xl text-[#0F172A]" data-testid="qualiopi-panneau-titre">{d.titre}</SheetTitle>
              <SheetDescription className="text-xs">{d.critere_titre}</SheetDescription>
            </SheetHeader>
            <Referentiel d={d} />
            <PreuvesAppli preuves={d.preuves_appli} onNavigate={onClose} />
            <LiensSection num={d.num} liens={d.liens} onUpdate={maj} />
            <ElementsAppliSection num={d.num} elements={d.elements_appli || []} onUpdate={maj} />
            <DocumentsSection num={d.num} documents={d.documents} onUpdate={maj} />
            <Commentaire d={d} onSave={enregistrer} />
            <NotesAuditSection num={d.num} notes={d.notes_audit || []} onUpdate={maj} />
            <AideIA num={d.num} aide={d.aide_ia} onAide={(aide) => setD((x) => ({ ...x, aide_ia: aide }))} />
            <HistoriqueSection historique={d.historique} />
            <PiedValidation d={d} busy={busy} onSave={enregistrer} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
